/**
 * Helpers shared by the filter dropdowns that hang off a button.
 *
 * A dropdown is absolutely positioned, so nothing stops it running past the
 * bottom of the screen - and on a short page there is no scroll left to bring
 * the rest of it back. These measure the room actually left around the button
 * and hold the page still while a menu is open.
 */

/** Gap left between the trigger button and the menu (`mt-2` / `mb-2`) */
const TRIGGER_GAP = 8;
/** Breathing room kept between the menu and the edge it stops at */
const EDGE_GAP = 16;
/** Below this much room, a menu looks for more space on the other side instead */
const DEFAULT_MIN_HEIGHT = 160;
/** Floor for the degenerate case where neither side has real room */
const ABSOLUTE_MIN_HEIGHT = 88;
/** How tall a menu grows when there is room to spare */
const DEFAULT_MAX_HEIGHT = 320;

export interface DropdownPlacement {
  /** Tallest the menu may be, in pixels, before it needs to scroll itself */
  maxHeight: number;
  /** Whether the menu opens upwards because there is more room above */
  above: boolean;
}

export interface DropdownPlacementOptions {
  /** Height the menu takes when the screen is roomy - defaults to 320 */
  maxHeight?: number;
  /** Room below the trigger under which the menu prefers to open upwards - defaults to 160 */
  minHeight?: number;
}

/**
 * The height the viewport actually shows. On mobile the visual viewport shrinks
 * as the browser chrome and keyboard come in, which makes it the safer of the two.
 * @returns The usable viewport height in pixels
 */
function viewportHeight(): number {
  return Math.min(window.innerHeight, window.visualViewport?.height ?? Number.POSITIVE_INFINITY);
}

/**
 * Height of an element that is on screen, or 0 when it is not rendered at this
 * breakpoint. Works for fixed and sticky elements, which report no offsetParent.
 * @param selector - CSS selector for the element
 * @returns Its rendered height in pixels
 */
function renderedHeight(selector: string): number {
  return document.querySelector(selector)?.getBoundingClientRect().height ?? 0;
}

/**
 * Where the page's sticky chrome ends, so a menu opening upwards stops below it
 * instead of sliding under the header or the category bar.
 * @returns The y position, in pixels from the top of the viewport
 */
function stickyChromeBottom(): number {
  const halfway = viewportHeight() / 2;

  return ['app-header', 'app-nav-bar'].reduce((lowest, selector) => {
    const rect = document.querySelector(selector)?.getBoundingClientRect();
    // The header is pinned at the top of the screen and the category bar just
    // under it; anything that is hidden at this breakpoint, or has scrolled
    // down the page, covers nothing
    if (!rect || rect.height === 0 || rect.top > halfway) return lowest;
    return Math.max(lowest, rect.bottom);
  }, 0);
}

/**
 * Works out how tall a dropdown can be, and which side of its button it should
 * open on, so the whole menu stays on screen at any size.
 * @param trigger - The button the menu hangs from
 * @param options - Preferred and minimum heights
 * @returns The height cap and the side to open on
 */
export function measureDropdownPlacement(
  trigger: HTMLElement,
  options: DropdownPlacementOptions = {}
): DropdownPlacement {
  const maxHeight = options.maxHeight ?? DEFAULT_MAX_HEIGHT;
  const minHeight = options.minHeight ?? DEFAULT_MIN_HEIGHT;

  const rect = trigger.getBoundingClientRect();
  // The fixed mobile tab bar sits over the bottom of the page, so the menu has
  // to stop above it rather than at the viewport edge
  const bottomEdge = viewportHeight() - renderedHeight('.mobile-tabbar');

  const spaceBelow = bottomEdge - rect.bottom - TRIGGER_GAP - EDGE_GAP;
  const spaceAbove = rect.top - stickyChromeBottom() - TRIGGER_GAP - EDGE_GAP;

  // Opening downwards is the norm; flipping up is only worth it when the menu
  // is squeezed below and genuinely has more room above
  const above = spaceBelow < Math.min(maxHeight, minHeight) && spaceAbove > spaceBelow;
  const space = above ? spaceAbove : spaceBelow;

  // The height is what the chosen side actually has: forcing a taller menu into
  // a short screen is what pushes one off the bottom or under the sticky header
  return {
    above,
    maxHeight: Math.round(Math.max(ABSOLUTE_MIN_HEIGHT, Math.min(maxHeight, space)))
  };
}

/** How many owners are currently holding the page still */
let lockHolders = 0;

/**
 * Takes the page's scrolling away. Hiding the overflow also takes the scrollbar
 * away, and on desktop the page would jump sideways by its width, so the same
 * width is handed back as padding while the lock is held.
 */
function applyBodyLock(): void {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
  document.body.style.overflow = 'hidden';
}

/** Gives the page back its scrolling, and the scrollbar its space */
function releaseBodyLock(): void {
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
}

/**
 * Holds the page still while a popup is open - a menu that scrolls inside itself
 * would otherwise drag the page along once the scroll runs past its end, and a
 * page sliding out from under an open menu leaves it detached from its button.
 *
 * Each owner keeps its own instance and the page is only released once every
 * owner has let go, so one popup closing cannot pull the page out from under
 * another that is still open.
 */
export class PageScrollLock {
  private locked = false;

  /**
   * @param locked - Whether this owner needs the page held still
   */
  set(locked: boolean): void {
    if (locked === this.locked) return;

    this.locked = locked;
    lockHolders += locked ? 1 : -1;

    if (locked && lockHolders === 1) {
      applyBodyLock();
    } else if (lockHolders === 0) {
      releaseBodyLock();
    }
  }
}
